/**
 * Tracking Service
 *
 * Single entry point for custom analytics events. Fans out to:
 *   - PostHog (primary product analytics spine + session replay)
 *   - Google Analytics (via analyticsService)
 *
 * Account email domains do not suppress analytics capture.
 */

import posthogService from "./posthogService";
import analyticsService from "./analyticsService";
import type { TrackingEventName, TrackingEventProps } from "./trackingEvents";

export interface TrackingUserTraits {
  email?: string | null;
  name?: string | null;
  role?: string;
  isProvider?: boolean;
  isAdmin?: boolean;
  providerId?: string;
  createdAt?: Date | string;
  [key: string]: unknown;
}

class TrackingService {
  canEvaluateAnonymousFlags(): boolean {
    if (!posthogService.isReady() || posthogService.isOptedOut()) return false;
    const deviceId = posthogService.getDeviceId();
    return deviceId !== null && posthogService.getDistinctId() === deviceId;
  }

  readFeatureFlag(key: string, fresh = false): boolean | string | undefined {
    return posthogService.readFeatureFlag(key, fresh);
  }

  onFeatureFlags(listener: (errorsLoading: boolean) => void): () => void {
    return posthogService.onFeatureFlags(listener);
  }

  recordFeatureFlagExposure(key: string, variant: string): boolean {
    return posthogService.recordFeatureFlagExposure(key, variant);
  }

  identify(userId: string, traits: TrackingUserTraits): void {
    // Recover capture state persisted by earlier versions that opted staff out.
    // Run this before identify so the identity update is sent as well.
    if (posthogService.isOptedOut()) {
      posthogService.optIn();
    }

    posthogService.identify(userId, traits);
    analyticsService.identifyUser(userId, traits);
  }

  track<E extends TrackingEventName>(event: E, props: TrackingEventProps = {}): void {
    posthogService.capture(event, props);
  }

  /**
   * Register session-scoped super properties that PostHog merges into
   * every subsequent `track()` / `capture()` call until either
   * `unregisterSuperProperty()` is called or the browser session ends.
   *
   * Intended for surface-scoped cohort tagging: a component registers
   * on mount and unregisters on unmount, and every event emitted during
   * that window -- including events from shared services like
   * `analyticsService` and `AuthContext` that do not accept per-call
   * metadata -- automatically carries the cohort fields. Do NOT use for
   * user traits (use `identify()` for those) or for per-event props
   * (pass them to `track()` directly).
   *
   * Session scope caps worst-case leakage at the current session if
   * cleanup never runs. The `TrackingEventProps` shape (scalars only)
   * is enforced on super properties too, for query parity with
   * per-event props.
   */
  registerSessionSuperProperties(props: TrackingEventProps): void {
    posthogService.registerForSession(props);
  }

  /**
   * Read a session super property: one set through
   * `registerSessionSuperProperties()`, or one posthog-js saved from the
   * landing URL (`utm_*`, ...). `undefined` when it is not set. For a caller
   * about to register over a key that must put the visitor's own value back
   * afterwards. Values are scalars, as `TrackingEventProps` requires.
   */
  getSessionSuperProperty(propertyName: string): TrackingEventProps[string] {
    return posthogService.getSessionProperty(propertyName) as TrackingEventProps[string];
  }

  /**
   * Undo `registerSessionSuperProperties()` for one key. Clears the session
   * register only and never persistent storage, so it cannot delete a
   * persistent property of the same name. Idempotent: safe to call for a key
   * that was never registered. Prefer this over letting super properties
   * expire at session end; without an explicit unregister, within-session
   * post-surface events will inherit the cohort tag and pollute non-surface
   * insights.
   */
  unregisterSuperProperty(propertyName: string): void {
    posthogService.unregisterForSession(propertyName);
  }

  page(
    path: string,
    title: string,
    context: {
      is_authenticated: boolean;
      user_role?: string;
      page_type?: string;
      content_group?: string;
    }
  ): void {
    // PostHog autocaptures $pageview on history change; emit a typed
    // wrapper event so custom props (page_type, content_group, user_role)
    // are queryable alongside autocapture for PostHog funnels/paths.
    posthogService.capture("nav_route_change", { path, title, ...context });
  }

  exception(error: Error, context: Record<string, unknown> = {}): void {
    posthogService.captureException(error, context);
  }

  reset(): void {
    // Delegates to analyticsService.reset() which (a) clears GA user_id
    // and GA user properties (so `is_internal`, `user_role`, etc. don't
    // leak across sessions on a shared device) and (b) calls
    // posthog.reset() which clears PostHog identity, session id, super
    // properties, and persisted capture state.
    // Calling posthog.opt_in_capturing() here would only emit a noisy
    // $opt_in event without changing behavior.
    analyticsService.reset();
  }
}

export const trackingService = new TrackingService();
export default trackingService;
